import { AllyAttribute, AllyBase, AllyId, AllyObject } from "../classes";
import { AllyNode } from "../types";

/**
 * This class contains functions specific to accessibility.
 * 
 * @extends AllyBase
 */
export class AllyAccessibility extends AllyBase {
    private id: string;
    private static ids: AllyId = new AllyId("lib", AllyBase.bridge);
    private ignoreAttribute: string;
    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase) {
        super(bridge);
        this.id = AllyAccessibility.ids.getId();
        this.destroyer.addCallback(() => {
            AllyAccessibility.ids.returnId(this.id);
        });
        this.ignoreAttribute = this.id + "-ignore";
    }
    /**
     * This function creates or changes the hidden text in a DOM node.
     * @param object The AllyObject whose node will contain the hidden text.
     * @param text The text to be written.
     */
    public writeHiddenText(object: AllyObject, text: string) {
        if (!object.variables.hiddenText) {
            this.createHiddenElement(object);
        }
        object.variables.hiddenText!.textContent = text;
    }
    /**
     * This function creates a hidden text node in the node of the AllyObject that is provided.
     * @param object The AllyObject whose node will contain the hidden text.
     * @returns Returns the hidden text node.
     */
    private createHiddenElement(object: AllyObject) {
        let element = document.createElement("div");
        element.style = "width: 0; height: 0; overflow: hidden";
        let innerElement = document.createElement("div")
        element.appendChild(innerElement);
        element.setAttribute(this.ignoreAttribute, "");
        let textNode = document.createTextNode("");
        innerElement.appendChild(textNode);
        object.node.appendChild(element);
        object.variables.hiddenText = textNode;
        return textNode;
    }
    /**
     * Checks if the provided node has the ignoreAttribute.
     * @param node The node to be checked
     * @returns Returns True if the node has the attribute otherwise it returns False or undefined.
     */
    public isIgnore(node: AllyNode) {
        return node.getAttribute && node.getAttribute(this.ignoreAttribute) === "";
    }
    /**
     * Sets the aria-hidden attribute of the node of a AllyObject to true or to false.
     * @param object The AllyObject whose nodes attribute is to be changed.
     * @param value The value the attribute is to be changed to.
     */
    public setAriaHidden(object: AllyObject, value: boolean) {
        if (object.variables[this.id + "-index"] === undefined) {
            object.variables[this.id + "-index"] = object.attribute.createIndex(0);
        }
        let index = object.variables[this.id + "-index"] as AllyAttribute["index"];
        if (value) {
            object.attribute.setAttribute("aria-hidden", index, "true");
        } else {
            object.attribute.setAttribute("aria-hidden", index, "false");
        }
    }
    /**
     * This function returns the aria-hidden attributes value of a node.
     * @param node The node the attribute value of is to be retrieved.
     * @returns Return the attribute value or undefined.
     */
    public getAriaHidden(node: AllyNode) {
        if (node.getAttribute) {
            if (node.getAttribute("aria-hidden") === "true") {
                return true;
            } else if (node.getAttribute("aria-hidden") === "false") {
                return false;
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }
    // preventScroll: focusing into a clipped container would scroll it into view.
    public focus(node: AllyNode) {
        node.focus?.({ preventScroll: true });
    }
    /**
     * Checks if the node of a AllyObject is a child node of a node with the attribute aria-hidden="true".
     * 
     * The node does not need to be a direct child node of the node that has the attribute aria-hidden="true".
     * 
     * @param object The AllyObject whose node is to be checked.
     * @returns Returns True if the node of the object is a child node of a node with the attribute aria-hidden="true".
     */
    public isAriaHidden(object: AllyObject) {
        return document.querySelector('[aria-hidden="true"] [adx-ally-id="' + object.id + '"]') !== null || document.querySelector('[aria-hidden="true"][adx-ally-id="' + object.id + '"]') !== null;
    }
}